import React, { useState, useEffect } from 'react';
import { Header } from '../components/Header';
import { StatusBadge } from '../components/StatusBadge';
import { PageLoader, ButtonLoader } from '../components/LoadingSpinner';
import { marketplaceAPI, coinsAPI } from '../lib/api';
import { formatNumber, formatDateTime } from '../lib/formatters';
import { ShoppingBag, Plus, TrendingUp, Tag, AlertTriangle, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export default function MarketplacePage() {
  const [listings, setListings] = useState([]);
  const [myListings, setMyListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('browse');
  const [coinBalance, setCoinBalance] = useState(0);
  const [actionLoading, setActionLoading] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const balanceRes = await coinsAPI.getBalance();
      setCoinBalance(balanceRes.data.balance);

      if (activeTab === 'browse') {
        const response = await marketplaceAPI.getListings({});
        setListings(response.data);
      } else {
        const response = await marketplaceAPI.getMyListings();
        setMyListings(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch marketplace data:', error);
      setError('Unable to load marketplace. Please try again.');
      toast.error('Failed to load marketplace');
    } finally {
      setLoading(false);
    }
  };

  const handleBuyNow = async (listing) => {
    if (coinBalance < listing.total_price) {
      toast.error('Insufficient coins!');
      return;
    }

    try {
      setActionLoading(listing.listing_id);
      await marketplaceAPI.createOffer({
        listing_id: listing.listing_id,
        offered_price_per_share: listing.price_per_share,
        shares_requested: listing.shares_available,
      });
      toast.success('Offer submitted! Waiting for seller approval.');
      fetchData();
    } catch (error) {
      console.error('Failed to make offer:', error);
      toast.error(error.response?.data?.detail || 'Failed to make offer');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancelListing = async (listingId) => {
    try {
      setActionLoading(listingId);
      await marketplaceAPI.cancelListing(listingId);
      toast.success('Listing cancelled');
      fetchData();
    } catch (error) {
      console.error('Failed to cancel listing:', error);
      toast.error('Failed to cancel listing');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRespondToOffer = async (offerId, action, counterPrice = null) => {
    try {
      setActionLoading(offerId);
      await marketplaceAPI.respondToOffer(offerId, {
        action,
        counter_price: counterPrice,
      });
      toast.success(action === 'accept' ? 'Offer accepted! Sale complete.' : 'Offer rejected');
      fetchData();
    } catch (error) {
      console.error('Failed to respond to offer:', error);
      toast.error(error.response?.data?.detail || 'Failed to process offer');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <Header />

      <main className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-[hsl(var(--text-primary))] mb-2">
            Share Marketplace
          </h1>
          <p className="text-[hsl(var(--text-secondary))]">
            Buy and sell shares from other employees
          </p>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-[hsl(var(--border))]">
            <nav className="flex gap-8">
              <button
                data-testid="tab-browse"
                onClick={() => setActiveTab('browse')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                  activeTab === 'browse'
                    ? 'border-[hsl(var(--brand-primary))] text-[hsl(var(--brand-primary))]'
                    : 'border-transparent text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--text-primary))]'
                }`}
              >
                Browse Listings
              </button>
              <button
                data-testid="tab-my-listings"
                onClick={() => setActiveTab('my-listings')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                  activeTab === 'my-listings'
                    ? 'border-[hsl(var(--brand-primary))] text-[hsl(var(--brand-primary))]'
                    : 'border-transparent text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--text-primary))]'
                }`}
              >
                My Listings
              </button>
            </nav>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <PageLoader text="Loading marketplace..." />
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-700 font-medium mb-2">Unable to load marketplace</p>
            <p className="text-red-600 text-sm mb-4">{error}</p>
            <button
              onClick={fetchData}
              className="inline-flex items-center gap-2 px-6 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Try Again
            </button>
          </div>
        ) : (
          <>
            {/* Browse Tab */}
            {activeTab === 'browse' && (
              <div>
                {listings.length === 0 ? (
                  <div className="bg-white border border-[hsl(var(--border))] rounded-xl p-12 text-center">
                    <ShoppingBag className="w-12 h-12 text-[hsl(var(--text-tertiary))] mx-auto mb-4" />
                    <p className="text-[hsl(var(--text-secondary))]">No listings available</p>
                    <p className="text-sm text-[hsl(var(--text-tertiary))] mt-2">
                      Check back later or list your own shares!
                    </p>
                  </div>
                ) : (
                  <div data-testid="marketplace-listings" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {listings.map((listing) => (
                      <div
                        key={listing.listing_id}
                        data-testid={`listing-${listing.listing_id}`}
                        className="bg-white border border-[hsl(var(--border))] rounded-xl p-6 hover:-translate-y-1 hover:shadow-lg transition-all duration-200"
                      >
                        {/* Asset Image */}
                        {listing.asset_image && (
                          <div className="mb-4 rounded-lg overflow-hidden h-32">
                            <img
                              src={listing.asset_image}
                              alt={listing.asset_name}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          </div>
                        )}

                        <h3 className="font-semibold text-[hsl(var(--text-primary))] text-lg mb-1">
                          {listing.asset_name || 'Asset'}
                        </h3>
                        <p className="text-xs font-mono text-[hsl(var(--text-tertiary))] mb-4">
                          #{listing.asset_unit}
                        </p>

                        {/* Listing Details */}
                        <div className="space-y-3 mb-4">
                          <div className="flex justify-between">
                            <span className="text-sm text-[hsl(var(--text-secondary))]">Shares Available</span>
                            <span className="font-mono font-semibold text-[hsl(var(--text-primary))]">
                              {listing.shares_available}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-[hsl(var(--text-secondary))]">Price per Share</span>
                            <span className="font-mono font-semibold text-[hsl(var(--brand-primary))]">
                              {formatNumber(listing.price_per_share)} coins
                            </span>
                          </div>
                          <div className="flex justify-between pt-2 border-t border-[hsl(var(--border))]">
                            <span className="text-sm font-medium text-[hsl(var(--text-secondary))]">Total Price</span>
                            <span className="font-mono font-bold text-[hsl(var(--text-primary))] text-lg">
                              {formatNumber(listing.total_price)} coins
                            </span>
                          </div>
                        </div>

                        {/* Seller Info */}
                        <div className="mb-4 p-3 bg-[hsl(var(--background-secondary))] rounded-lg">
                          <div className="text-xs text-[hsl(var(--text-secondary))]\">Seller</div>
                          <div className="text-sm font-medium text-[hsl(var(--text-primary))]">
                            {listing.seller_name}
                          </div>
                          <div className="text-xs text-[hsl(var(--text-tertiary))]">
                            Listed {formatDateTime(listing.created_at)}
                          </div>
                        </div>

                        {/* Action Button */}
                        <button
                          data-testid={`buy-now-${listing.listing_id}`}
                          onClick={() => handleBuyNow(listing)}
                          className="w-full bg-[hsl(var(--brand-primary))] hover:bg-[hsl(var(--brand-primary-dark))] text-white font-semibold px-6 py-3 rounded-lg transition-colors duration-200"
                        >
                          Make Offer
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* My Listings Tab */}
            {activeTab === 'my-listings' && (
              <div>
                <div className="mb-4 flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-[hsl(var(--text-primary))]">
                    My Active Listings
                  </h2>
                  <button
                    onClick={() => navigate('/portfolio')}
                    className="flex items-center gap-2 px-4 py-2 bg-[hsl(var(--brand-primary))] hover:bg-[hsl(var(--brand-primary-dark))] text-white font-semibold rounded-lg transition-colors duration-200"
                  >
                    <Plus className="w-4 h-4" />
                    Create Listing
                  </button>
                </div>

                {myListings.length === 0 ? (
                  <div className="bg-white border border-[hsl(var(--border))] rounded-xl p-12 text-center">
                    <Tag className="w-12 h-12 text-[hsl(var(--text-tertiary))] mx-auto mb-4" />
                    <p className="text-[hsl(var(--text-secondary))]">No active listings</p>
                    <button
                      onClick={() => navigate('/portfolio')}
                      className="mt-4 px-6 py-2 bg-[hsl(var(--brand-primary))] hover:bg-[hsl(var(--brand-primary-dark))] text-white font-semibold rounded-lg transition-colors duration-200"
                    >
                      Create Your First Listing
                    </button>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {myListings.map((listing) => (
                      <div
                        key={listing.listing_id}
                        className="bg-white border border-[hsl(var(--border))] rounded-xl p-6"
                      >
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="font-semibold text-[hsl(var(--text-primary))] text-lg">
                              {listing.asset_name}
                            </h3>
                            <p className="text-sm text-[hsl(var(--text-secondary))]">
                              {listing.shares_available} shares @ {formatNumber(listing.price_per_share)} coins/share
                            </p>
                          </div>
                          <StatusBadge status={listing.status} />
                        </div>

                        {/* Offers */}
                        {listing.offers && listing.offers.length > 0 && (
                          <div className="mb-4">
                            <h4 className="text-sm font-medium text-[hsl(var(--text-primary))] mb-2">
                              Offers ({listing.offer_count})
                            </h4>
                            <div className="space-y-2">
                              {listing.offers.map((offer) => (
                                <div
                                  key={offer.offer_id}
                                  className="p-3 bg-[hsl(var(--background-secondary))] rounded-lg"
                                >
                                  <div className="flex justify-between items-center">
                                    <div>
                                      <div className="text-sm font-medium text-[hsl(var(--text-primary))]">
                                        {offer.buyer_name}
                                      </div>
                                      <div className="text-xs text-[hsl(var(--text-secondary))]">
                                        {offer.shares_requested} shares @ {formatNumber(offer.offered_price_per_share)} coins
                                      </div>
                                    </div>
                                    {offer.status === 'pending' && (
                                      <div className="flex gap-2">
                                        <button
                                          data-testid={`accept-offer-${offer.offer_id}`}
                                          onClick={() => handleRespondToOffer(offer.offer_id, 'accept')}
                                          className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm rounded transition-colors duration-200"
                                        >
                                          Accept
                                        </button>
                                        <button
                                          onClick={() => handleRespondToOffer(offer.offer_id, 'reject')}
                                          className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-sm rounded transition-colors duration-200"
                                        >
                                          Reject
                                        </button>
                                      </div>
                                    )}
                                    {offer.status !== 'pending' && (
                                      <StatusBadge status={offer.status} />
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Actions */}
                        {listing.status === 'active' && (
                          <button
                            data-testid={`cancel-listing-${listing.listing_id}`}
                            onClick={() => handleCancelListing(listing.listing_id)}
                            className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg text-sm font-medium text-[hsl(var(--text-secondary))] hover:bg-[hsl(var(--background-secondary))] transition-colors duration-200"
                          >
                            Cancel Listing
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
